import {
  IRequestTaller,
  IEdicionAdmin,
  IEdicionUsuario,
  TipoElencoEdicionRequest,
  IFormBaja,
  ConfirmacionRegistro,
  ResetearPass,
  IActualizarPass,
} from 'utils/models/interfaces';
import { API_BASE_URL } from './constants';
import { axiosInstance, getSkipAuthHeaders, setupInterceptors } from './apiUtils';
import { IAltaFunciones, IRequestEspectaculo, TipoParametro } from 'utils/models/types';
import { espectaculoPath } from './servicioPost';
import axios from 'axios';
import { obtenerError } from 'utils/utilities';
import { getUsuarioPorId } from './servicioGet';

// Definición de URLs
const edicionUsuarioPath = `${API_BASE_URL}/Usuarios/datos/`;
const edicionAdminPath = `${API_BASE_URL}/Usuarios/cat/`;
const registroFuncionesPath = `${espectaculoPath}/addfunciones`;
const edicionEspectaculoPath = `${espectaculoPath}/`;
const edicionTipoElencoPath = `${API_BASE_URL}/TiposElenco/`;
const edicionTallerPath = `${API_BASE_URL}/Talleres/`;
const bajaUsuarioPath = `${API_BASE_URL}/Usuarios/inact/`;
const confirmacionRegistroPath = `${API_BASE_URL}/Usuarios/confirmar-registro`;
const actualizarPassPath = `${API_BASE_URL}/Usuarios/restablecerPass`;
const cambiarPassPath = `${API_BASE_URL}/Usuarios/actualizarPass`;
const cambiarParametroPorIdPath = `${API_BASE_URL}/Parametros/`;

// Función genérica PUT
export const putData = async <T>(
  url: string,
  data: unknown,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void,
  formData?: boolean
): Promise<T | null> => {
  try {
    setLoading(true);
    let response;
    if (formData) {
      response = await axiosInstance.put<T>(url, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } else {
      response = await axiosInstance.put<T>(url, data);
    }
    return response.data;
  } catch (error) {
    console.error(`Error de edición en ${url}:`, error);
    if (axios.isAxiosError(error)) {
      await obtenerError(error, setError);
    } else {
      const genericErrorMessage = 'Error desconocido';
      setError(genericErrorMessage);
      console.error(genericErrorMessage, error);
    }
    return null; // Retorna null en caso de error
  } finally {
    setLoading(false);
  }
};

// Registro de funciones de espectáculo
export const registroFunciones = async (
  objetoAltaFunciones: IAltaFunciones,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void
) => {
  await putData<IAltaFunciones>(registroFuncionesPath, objetoAltaFunciones, setError, setLoading);
};

// Edición de usuario
export const edicionUsuarioPut = async (
  objetoEdUsu: IEdicionUsuario,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void
) => {
  const { Id } = objetoEdUsu;
  const result = await putData<IEdicionUsuario>(`${edicionUsuarioPath}${Id}`, objetoEdUsu, setError, setLoading);
  if (result) {
    const usuarioEditado = await getUsuarioPorId(Id.toString());
    localStorage.setItem('usuario', JSON.stringify(usuarioEditado));
    setupInterceptors();
  }
};

// Baja de usuario
export const bajaUsuarioPut = async (
  objetoBajaUsu: IFormBaja,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void
) => {
  const { idUsuario } = objetoBajaUsu;
  await putData<IEdicionUsuario>(`${bajaUsuarioPath}${idUsuario}`, objetoBajaUsu, setError, setLoading);
};

// Edición de administrador
export const edicionAdminPut = async (
  objetoEdAdmin: IEdicionAdmin,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void
) => {
  const { Id } = objetoEdAdmin;
  const result = await putData<IEdicionAdmin>(`${edicionAdminPath}${Id}`, objetoEdAdmin, setError, setLoading);
  if (result) {
    // Si el usuario editado por el admin es el mismo admin, actualizar los datos en el local storage
    const logueadoId = localStorage.getItem('usuarioId');
    if (Id === Number(logueadoId)) {
      const usuarioEditado = logueadoId && (await getUsuarioPorId(logueadoId));
      localStorage.setItem('usuario', JSON.stringify(usuarioEditado));
    }
  }
};

// Registro de nuevos tipos de elenco
export const edicionTipoElenco = async (
  objetoEdicionTipoElenco: TipoElencoEdicionRequest,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void
) => {
  const { IdTipoElenco } = objetoEdicionTipoElenco;
  await putData<TipoElencoEdicionRequest>(
    `${edicionTipoElencoPath}${IdTipoElenco}`,
    objetoEdicionTipoElenco,
    setError,
    setLoading
  );
};

// Edicion de espectáculo
export const edicionEspectaculo = async (
  objetoEdicionEspectaculo: IRequestEspectaculo,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void,
  formData?: boolean,
  formDataObject?: FormData
) => {
  const { Id } = objetoEdicionEspectaculo;

  await putData<IRequestEspectaculo>(
    `${edicionEspectaculoPath}${Id}`,
    formData ? formDataObject : objetoEdicionEspectaculo,
    setError,
    setLoading,
    formData
  );
};

export const edicionTaller = async (
  objetoEdicionTaller: IRequestTaller,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void,
  formData: boolean,
  formDataObject?: FormData
) => {
  const { Id } = objetoEdicionTaller;
  await putData<IRequestTaller>(
    `${edicionTallerPath}${Id}`,
    formData ? formDataObject : objetoEdicionTaller,
    setError,
    setLoading,
    formData
  );
};

export const confirmacionRegistro = async (
  objetoConfirmacion: ConfirmacionRegistro,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void
) => {
  try {
    setLoading(true);
    const response = await axiosInstance.put<string>(confirmacionRegistroPath, objetoConfirmacion, {
      headers: getSkipAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error(`Error de confirmacion en ${confirmacionRegistroPath}:`, error);
    if (axios.isAxiosError(error)) {
      await obtenerError(error, setError);
    } else {
      const genericErrorMessage = 'Error desconocido';
      setError(genericErrorMessage);
      console.error(genericErrorMessage, error);
    }
    return null;
  } finally {
    setLoading(false);
  }
};

export const actualizarPass = async (
  objetoPassword: ResetearPass,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void
) => {
  await putData<string>(actualizarPassPath, objetoPassword, setError, setLoading);
};

export const cambiarPass = async (
  objetoCambiarPassword: IActualizarPass,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void
) => {
  await putData<string>(cambiarPassPath, objetoCambiarPassword, setError, setLoading);
};

export const cambiarParametroPorId = async (
  objetoParametro: TipoParametro,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void
) => {
  const { Id } = objetoParametro;
  await putData(`${cambiarParametroPorIdPath}${Id}`, objetoParametro, setError, setLoading);
};
